<template>
  <div class="feature-comparison-container">
    <!-- Full Comparison Dialog -->
    <v-dialog v-model="showFullComparisonDialog" max-width="900" content-class="comparison-dialog">
      <v-card class="comparison-dialog-card">
        <!-- Sticky Dialog Header -->
        <div class="sticky-dialog-header">
          <div class="d-flex align-center">
            <h2 class="dialog-title">Compare all features</h2>
            <v-spacer></v-spacer>
            <v-btn icon aria-label="Close" @click="showFullComparisonDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="table-container">
          <!-- Sticky Table Header -->
          <div class="sticky-table-header">
            <table class="feature-comparison-table">
              <tr>
                <th class="feature-column">FEATURES</th>
                <th class="feature-column">FREE</th>
                <th class="feature-column">PROFESSIONAL</th>
                <th class="feature-column">REPLENISHMENT CENTER</th>
              </tr>
            </table>
          </div>
          
          <!-- Scrollable Table Body -->
          <div class="scrollable-table-body">
            <table class="feature-comparison-table">
              <!-- Enterprise Features Section -->
              <tr class="feature-category-row">
                <td colspan="4" class="font-weight-bold">Replenishment-Center-Only Features</td>
              </tr>
              <tr v-for="feature in fullEnterpriseOnlyFeatures" :key="feature.name" class="feature-row">
                <td class="feature-column">{{ feature.name }}</td>
                <td class="feature-column">{{ feature.free }}</td>
                <td class="feature-column">{{ feature.professional }}</td>
                <td class="feature-column">{{ feature.enterprise }}</td>
              </tr>
              
              <!-- Professional & Enterprise Features Section -->
              <tr class="feature-category-row">
                <td colspan="4" class="font-weight-bold">Professional & Replenishment Center Only Features</td>
              </tr>
              <tr v-for="feature in fullProfessionalAndEnterpriseFeatures" :key="feature.name" class="feature-row">
                <td class="feature-column">{{ feature.name }}</td>
                <td class="feature-column">{{ feature.free }}</td>
                <td class="feature-column">{{ feature.professional }}</td>
                <td class="feature-column">{{ feature.enterprise }}</td>
              </tr>
              
              <!-- Available for All Plans Section -->
              <tr class="feature-category-row">
                <td colspan="4" class="font-weight-bold">Available for All Plans</td>
              </tr>
              <tr v-for="feature in fullAllPlansFeatures" :key="feature.name" class="feature-row">
                <td class="feature-column">{{ feature.name }}</td>
                <td class="feature-column">{{ feature.free }}</td>
                <td class="feature-column">{{ feature.professional }}</td>
                <td class="feature-column">{{ feature.enterprise }}</td>
              </tr>
            </table>
          </div>
          
          <!-- Sticky Price Footer -->
          <div class="sticky-table-footer">
            <table class="feature-comparison-table">
              <tr class="pricing-row">
                <td class="feature-column font-weight-bold">Monthly Price*</td>
                <td class="feature-column price-cell">$0</td>
                <td class="feature-column price-cell">
                  <span v-if="professionalPrice">{{ professionalPrice }}</span>
                  <span v-else>Varies</span>
                </td>
                <td class="feature-column price-cell">
                  <span v-if="enterprisePrice">{{ enterprisePrice }}</span>
                  <span v-else>Varies</span>
                </td>
              </tr>
            </table>
            <div class="price-footnote">
              * Pricing follows your Shopify plan. Paid plans are billed per store, per month.
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { rcAiComparisonRows } from '@/components/featureComparisonRows';

export default {
  name: 'FeatureComparisonTable',
  props: {
    professionalPrice: {
      type: String,
      default: null
    },
    enterprisePrice: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showFullComparisonDialog: false,
      
      // Full comparison for the dialog
      fullEnterpriseOnlyFeatures: [
        ...rcAiComparisonRows(),
        { name: 'Advanced Buying Table', free: '−', professional: '−', enterprise: '✓' },
        { name: 'Supplier Management', free: '−', professional: '−', enterprise: '✓' },
        { name: 'Purchase Orders & Receiving', free: '−', professional: '−', enterprise: '✓' },
        { name: 'Replenish from Warehouse', free: '−', professional: '−', enterprise: '✓' },
        { name: 'Advanced Spreadsheet Integration', free: '−', professional: '−', enterprise: '✓' },
        { name: 'QuickBooks Integration', free: '−', professional: '−', enterprise: '✓' },
      ],
      fullProfessionalAndEnterpriseFeatures: [
        { name: 'AI & ML Demand Forecasting', free: '−', professional: '✓', enterprise: '✓' },
        { name: 'Buying Calendar', free: '−', professional: '✓', enterprise: '✓' },
        { name: 'Advanced Inventory Tracking', free: '−', professional: '✓', enterprise: '✓' },
        { name: 'Inventory Optimization', free: '−', professional: '✓', enterprise: '✓' },
        { name: 'Reorder Suggestion Report', free: '−', professional: '✓', enterprise: '✓' },
        { name: 'Product Segmentation', free: '−', professional: '✓', enterprise: '✓' },
        { name: 'Report Scheduling', free: '−', professional: '✓', enterprise: '✓' },
        { name: 'Basic Google Spreadsheet Integration', free: '−', professional: '✓', enterprise: '✓' },
        { name: 'Export Data', free: 'Current page only', professional: 'Full data export', enterprise: 'Full data export' }
      ],
      fullAllPlansFeatures: [
        { name: 'Basic Sales Analytics', free: '✓', professional: '✓', enterprise: '✓' },
        { name: 'Basic Dashboard', free: '✓', professional: '✓', enterprise: '✓' },
        { name: 'Custom Reports', free: '✓', professional: '✓', enterprise: '✓' },
        { name: 'Basic Inventory Tracking', free: '✓', professional: '✓', enterprise: '✓' }
      ]
    }
  }
}
</script>

<style scoped>
.feature-comparison-container {
  margin: 24px 0;
}

.feature-comparison-card {
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f7fa;
  transition: all 0.3s ease;
}

.feature-comparison-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.comparison-header {
  min-height: 80px;
}

.comparison-button {
  font-weight: 500;
  letter-spacing: 0.5px;
  min-width: 240px;
  height: 48px;
}

.comparison-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Dialog styling */
.comparison-dialog {
  overflow: hidden;
}

.comparison-dialog-card {
  display: flex;
  flex-direction: column;
  height: 90vh;
  max-height: 900px;
  overflow: hidden;
  border-radius: 14px;
}

/* Table container structure */
.table-container {
  display: flex;
  flex-direction: column;
  height: calc(90vh - 60px); /* Subtract header height */
  position: relative;
}

/* Sticky Dialog Header */
.sticky-dialog-header {
  background-color: #FFFFFF;
  border-bottom: 1px solid #E2E4E6;
  padding: 16px 24px;
  position: sticky;
  top: 0;
  z-index: 10;
  height: 60px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 700;
  color: #12222D;
  letter-spacing: -0.01em;
}

.sticky-dialog-header .v-btn:focus-visible {
  outline: 3px solid #2592B0;
  outline-offset: 2px;
}

/* Sticky Table Header */
.sticky-table-header {
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: #F7F8F8;
  border-bottom: 1px solid #E2E4E6;
}

.sticky-table-header th {
  padding: 12px 16px;
  font-weight: 700;
  color: #565D63;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-align: center;
}

.sticky-table-header th:first-child {
  text-align: left;
}

.sticky-table-header th:last-child {
  color: #1B7A95;
  background-color: #ECF8FC;
}

/* Scrollable Table Body */
.scrollable-table-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 24px;
}

/* Sticky Table Footer */
.sticky-table-footer {
  position: sticky;
  bottom: 0;
  z-index: 5;
  background-color: white;
  border-top: 2px solid #E2E4E6;
  flex-shrink: 0;
}

/* Consistent Table Structure */
.feature-comparison-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

/* Column widths - fixed and consistent */
.feature-column {
  width: 50%;
  text-align: center;
  padding: 10px 16px;
  font-size: 14.5px;
  color: #3A4147;
}

.feature-column:first-child {
  text-align: left;
}

/* RC column highlight (last column, all sections) */
.feature-row .feature-column:last-child,
.pricing-row .feature-column:last-child {
  background-color: #ECF8FC;
  color: #1B7A95;
  font-weight: 700;
}

.plan-column {
  width: 16.67%;
  text-align: center;
  padding: 10px 16px;
}

/* Row styling */
.feature-row td {
  border-bottom: 1px solid #F1F2F3;
}

.feature-category-row {
  background-color: #F7F8F8;
}

.feature-category-row td {
  color: #12222D;
  font-size: 13px;
  font-weight: 700;
  border-top: 1px solid #E2E4E6;
  border-bottom: 1px solid #E2E4E6;
  padding: 10px 16px;
}

.feature-row:hover {
  background-color: #F7F8F8;
}

.feature-row:hover .feature-column:last-child {
  background-color: #D4EFF7;
}

.pricing-row {
  background-color: #F7F8F8;
}

.price-cell {
  font-weight: 700;
  color: #2E7D32;
}

.price-footnote {
  font-size: 12px;
  color: #565D63;
  padding: 12px 16px;
  border-top: 1px solid #E2E4E6;
  text-align: left;
  background-color: white;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .feature-column {
    width: 40%;
    padding: 12px 8px;
    font-size: 14px;
  }
  
  .plan-column {
    width: 20%;
    padding: 12px 8px;
    font-size: 14px;
  }
  
  .sticky-table-header th,
  .feature-row td,
  .pricing-row td {
    padding: 12px 8px;
    font-size: 14px;
  }
}
</style> 